import { Analytics } from '@util/Analytics';
import { translate } from '@util/Localization';
import { Form, Input } from 'antd';
import React, { FunctionComponent, useState } from 'react';
import styled, { css } from 'styled-components';
import { Color } from '../../../config/Color';
import { appOverride } from '../../../util/AppUtils';
import { AppCheckbox } from '../../styles/Checkbox';
import { CopyContainer } from '../../styles/CopyContainer';
import { NextButton } from '../../styles/LayoutButtons';
import { HighlightLink } from '../../styles/Link';
import { textInputStyles } from '../../styles/TextInput';
import { BodyXS, ErrorBody, Heading } from '../../styles/Typography';

export type SubmitData = { name: string; email: string; password: string };

export type FinishAccountLayoutConfig = {
  subtitle: string;
  primaryButtonTitle: string;
};

type Props = FinishAccountLayoutConfig & {
  name: string | undefined;
  email: string;
  onSubmit: (values: SubmitData) => void;
  loading: boolean;
  error: string | undefined;
};

export const FinishAccountLayout: FunctionComponent<Props> = (props) => {
  const { subtitle, email, primaryButtonTitle, onSubmit, loading, error } = props;

  const [name, setName] = useState<string>(props.name ?? '');
  const [password, setPassword] = useState<string>('');
  const [accept, setAccept] = useState<boolean>(false);

  return (
    <CopyContainer>
      <Heading>{translate('payment.finishAccount.title')}</Heading>
      <BodyXS>{subtitle}</BodyXS>
      <Form onFinish={() => accept && onSubmit({ name, email, password })}>
        <Form.Item>
          <StyledInput
            size="large"
            autoComplete="name"
            placeholder="Name"
            value={name}
            data-test="finish-account-name"
            onChange={(e) => setName(e.target.value)}
          />
        </Form.Item>
        <Form.Item>
          <StyledInput size="large" placeholder="Email" value={email} disabled />
        </Form.Item>
        <Form.Item>
          <StyledPasswordInput
            size="large"
            autoComplete="new-password"
            placeholder="Password (min 6 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Form.Item>
        <Form.Item>
          <div>
            <InfoText>{translate('payment.finishAccount.info')}</InfoText>
            <AcceptCheckbox
              id="finish-accept"
              checked={accept}
              onChange={() => {
                setAccept(!accept);
              }}
            />
            <AcceptLabel htmlFor="finish-accept">
              <span>
                I accept the{' '}
                <HighlightLink
                  onClick={() => Analytics.track({ event: Analytics.Event.o_terms_opened })}
                  href={translate('links.terms')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms
                </HighlightLink>{' '}
                &amp;{' '}
                <HighlightLink
                  onClick={() => Analytics.track({ event: Analytics.Event.o_privacy_opened })}
                  href={translate('links.privacy')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </HighlightLink>
              </span>
            </AcceptLabel>
          </div>
        </Form.Item>
        {error === undefined && <ErrorBody>{error}</ErrorBody>}
        <Form.Item>
          <NextButton
            hideArrow
            loading={loading}
            disabled={!accept || name === '' || password.length < 6}
          >
            {primaryButtonTitle}
          </NextButton>
        </Form.Item>
      </Form>
    </CopyContainer>
  );
};

const styledTextInputStyle = css`
  ${textInputStyles}
  &, &:focus, &:hover {
    text-align: left;
    border-width: 1px;
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
`;

const StyledInput = styled(Input)`
  ${styledTextInputStyle}

  /* When input has value, show white border for Nerva */
  &:not(:placeholder-shown) {
    border-color: ${appOverride({ nerva: Color.content.default, default: 'inherit' })} !important;
  }

  /* When disabled (email field), maintain the filled-in styling */
  &:disabled {
    border-color: ${appOverride({ nerva: Color.content.default, default: 'inherit' })} !important;
    background-color: transparent;
    color: ${appOverride({ nerva: Color.content.default, default: 'inherit' })};
    cursor: not-allowed;
  }
`;

const StyledPasswordInput = styled(Input.Password)`
  ${styledTextInputStyle}

  input {
    background-color: transparent;
    color: ${appOverride({ nerva: Color.content.default, default: 'inherit' })};

    &::placeholder {
      color: ${appOverride({ nerva: '#f5f5f5', default: Color.content.default })};
      opacity: 0.25;
    }
  }

  /* When password input has value, show white border for Nerva */
  input:not(:placeholder-shown) {
    border-color: ${appOverride({ nerva: Color.content.default, default: 'inherit' })};
  }

  /* When wrapper has value (targets the password wrapper), show white border */
  &:has(input:not(:placeholder-shown)) {
    border-color: ${appOverride({ nerva: Color.content.default, default: 'inherit' })} !important;
  }

  /* Password visibility icon styling for Nerva */
  .ant-input-password-icon {
    color: ${appOverride({ nerva: '#f5f5f5', default: 'inherit' })};
    opacity: ${appOverride({ nerva: '0.25', default: '1' })};

    &:hover {
      color: ${appOverride({ nerva: '#f5f5f5', default: 'inherit' })};
      opacity: ${appOverride({ nerva: '0.25', default: '1' })};
    }
  }

  /* When input has value, make icon fully opaque white */
  &.ant-input-affix-wrapper-focused .ant-input-password-icon,
  input:not(:placeholder-shown) ~ .ant-input-suffix .ant-input-password-icon {
    color: ${appOverride({ nerva: Color.content.default, default: 'inherit' })};
    opacity: 1;
  }
`;

const InfoText = styled.p`
  color: ${appOverride({ nerva: Color.content.default, default: 'inherit' })};
`;

const AcceptLabel = styled.label`
  color: ${appOverride({ nerva: Color.content.default, default: 'inherit' })};
`;

const AcceptCheckbox = styled(AppCheckbox)`
  margin: ${({ theme }) => `0 ${theme.spacing._20px}`};
`;
